/**
 * 複数書類の確認（混在の可能性があるファイル）の一覧。
 * 作業トレイの「複数書類の確認」から `/documents?view=mixed` で開く。
 * ここでは **分割も新規作成もしない** — 判断を記録するだけ（API も確認のみ）。
 */
import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Stack, ArrowSquareOut } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/dms/mixed-review`;

export default function MixedReviewList({ onOpenDoc }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState('');

  const load = useCallback(async () => {
    try { setData((await axios.get(`${API}/pending`)).data); }
    catch (e) { toast.error(e?.response?.data?.detail || '確認待ちを取得できませんでした'); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const decide = async (id, decision) => {
    setBusy(id);
    try {
      const { data: out } = await axios.post(`${API}/${id}/decision`, { decision });
      toast.success(out.message_ja || '記録しました');
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || '記録できませんでした'); }
    finally { setBusy(''); }
  };

  const items = data?.items || [];
  return (
    <div data-testid="dms-mixed-review">
      <div className="mb-4">
        <h2 className="text-base font-bold text-zinc-900">複数書類の確認</h2>
        <p className="mt-0.5 text-sm text-zinc-500">
          {data?.notice_ja || '1つのファイルに複数種類の書類が含まれている可能性があります。'}
          <span className="ml-1 text-zinc-400">分割や新しい書類の作成は行いません。</span>
        </p>
      </div>

      {!data && <p className="text-sm text-zinc-400" data-testid="dms-mixed-loading">読込中…</p>}
      {data && items.length === 0 && (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-5 text-sm text-emerald-800"
          data-testid="dms-mixed-empty">確認が必要な書類はありません</div>
      )}

      <ul className="space-y-2" data-testid="dms-mixed-list">
        {items.map((it) => (
          <li key={it.document_id}
            className="rounded-lg border border-zinc-200 bg-white px-4 py-3"
            data-testid={`dms-mixed-item-${it.document_id}`}>
            <div className="flex flex-wrap items-start gap-3">
              <Stack size={18} weight="duotone" className="mt-0.5 shrink-0 text-zinc-400" />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold text-zinc-900">
                  {it.document_number && (
                    <span className="mr-1.5 font-mono text-xs text-zinc-500">{it.document_number}</span>
                  )}
                  {it.title}
                </p>
                <p className="mt-0.5 text-xs text-zinc-500">
                  含まれている可能性: <span className="font-semibold text-zinc-700">{it.segment_count}種類</span>
                  {it.confidence != null && ` ・ 確度 ${Math.round(it.confidence * 100)}%`}
                  {it.decision_stale_note_ja && (
                    <span className="ml-1 text-amber-700">{it.decision_stale_note_ja}</span>
                  )}
                </p>
              </div>
              <div className="flex flex-wrap gap-1.5">
                <Button size="sm" variant="outline" className="h-8 text-xs"
                  onClick={() => onOpenDoc?.(it.document_id)}
                  data-testid={`dms-mixed-open-${it.document_id}`}>
                  <ArrowSquareOut size={13} className="mr-1" /> 内容を確認する
                </Button>
                <Button size="sm" className="h-8 bg-teal-700 text-xs hover:bg-teal-800"
                  disabled={busy === it.document_id}
                  onClick={() => decide(it.document_id, 'keep_single')}
                  data-testid={`dms-mixed-keep-${it.document_id}`}>
                  1つの書類として扱う
                </Button>
                <Button size="sm" variant="outline" className="h-8 text-xs"
                  disabled={busy === it.document_id}
                  onClick={() => decide(it.document_id, 'review_later')}
                  data-testid={`dms-mixed-later-${it.document_id}`}>
                  後で確認する
                </Button>
              </div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
